import { ViewpointBuilderContext } from 'viewpointBuilder/types';
import {
  SelectViewpointBuilderContextPayload,
  SelectTabPayload,
  SetSelectedComponentTypeAndCountPayload,
  SetGraphCountsAndComponentTypeRepresentationPayload,
  SetFormattingRulesCount,
  SetGroupingRulesCount,
  SetSavedViewpointDetails,
  SetCollapsedPathsCount,
  SetRequiredComponentsCount,
} from './actions';
import {
  createViewpointMenuItems,
  editViewpointMenuItems,
  metamodelInformationMenuItem,
  openDatasetMenuItems,
} from './viewpointBuilderNavigationMenuItems';
import { getPopoverContent, isTabItemDisabled } from './isTabItemDisabled';
import { getTagsForNavMenuItem } from './getTagsForNavMenuItem';
import {
  ViewpointBuilderNavigationState,
  ViewpointBuilderNavigationViewModel,
  NavigationMode,
  ViewpointBuilderTabItem,
  ViewpointBuilderTabItemMeta,
} from './types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

const getInitialState = (): ViewpointBuilderNavigationState => {
  return {
    selectedTab: 'SELECT_CONTEXT_COMPONENT_INSTANCES_TAB',
    context: 'editSubgraph',
    selectedComponentTypes: [],
    selectedComponentCount: 0,
    filtersCount: 0,
    edgesCount: 0,
    nodesCount: 0,
    selectedComponentTypeRepresentation: null,
    conditionalFormattingRulesCount: 0,
    labelFormattingRulesCount: 0,
    groupingRulesCount: 0,
    collapsedPathsCount: 0,
    requiredComponentsCount: 0,
    viewpointName: null,
  };
};

const getInitialViewModel = (): ViewpointBuilderNavigationViewModel => {
  // i18nLocale$.state OK here: initial value for getInitialViewModel, reactive updates come via combineLatest in viewpointBuilderNavigation$
  return viewpointBuilderNavigationOperations.composeViewModel(
    viewpointBuilderNavigationOperations.getInitialState(),
    i18nLocale$.state
  );
};

const getTabItems = (
  state: ViewpointBuilderNavigationState,
  i18nLocale: I18nLocale
): ViewpointBuilderTabItem[] => {
  return getMenuListBasedOnContext(state, i18nLocale).map(item => ({
    ...item,
    isSelected: item.tabId === state.selectedTab,
    isDisabled: isTabItemDisabled(item.tabId, state),
    popoverContentForDisabledItem: getPopoverContent({
      item,
      state,
      i18nLocale,
    }),
    tags: getTagsForNavMenuItem(item.tabId, state),
  }));
};

const getMenuListBasedOnContext = (
  state: ViewpointBuilderNavigationState,
  i18nLocale: I18nLocale
): ViewpointBuilderTabItemMeta[] => {
  if (state.context === 'createViewpoint') {
    return createViewpointMenuItems(i18nLocale);
  }

  if (getModeFromContext(state.context) === 'simplified') {
    return openDatasetMenuItems(i18nLocale);
  }

  return editViewpointMenuItems(i18nLocale);
};

const composeViewModel = (
  state: ViewpointBuilderNavigationState,
  i18nLocale: I18nLocale
): ViewpointBuilderNavigationViewModel => {
  const metamodelItem = metamodelInformationMenuItem(i18nLocale);
  return {
    tabItems: getTabItems(state, i18nLocale),
    navigationMode: getModeFromContext(state.context),
    sidebarTitle: getSidebarTitleFromContext(state.context, i18nLocale),
    sidebarSubtitle: getSidebarSubtitleFromContext(state, i18nLocale),
    bottomItem: {
      ...metamodelItem,
      isSelected: metamodelItem.tabId === state.selectedTab,
      isDisabled: false,
      popoverContentForDisabledItem: undefined,
      tags: [],
    },
    ...state,
  };
};

const getModeFromContext = (
  context: ViewpointBuilderContext
): NavigationMode => {
  switch (context) {
    case 'editViewpoint':
    case 'createViewpoint':
      return 'advanced';

    case 'loadNewViewpoint':
    case 'openViewpoint':
    case 'editSubgraph':
    case 'contextMenu':
      return 'simplified';

    default:
      context satisfies never;
      return 'simplified';
  }
};

export const getSidebarTitleFromContext = (
  context: ViewpointBuilderContext,
  i18nLocale: I18nLocale
): string => {
  switch (context) {
    case 'editSubgraph':
      return t(
        i18nLocale,
        'viewpointBuilder:navigation.sidebarTitle.editDataset',
        'Edit dataset'
      );

    case 'editViewpoint':
      return t(
        i18nLocale,
        'viewpointBuilder:navigation.sidebarTitle.editViewpoint',
        'Edit Viewpoint'
      );

    case 'createViewpoint':
      return t(
        i18nLocale,
        'viewpointBuilder:navigation.sidebarTitle.createViewpoint',
        'Create Viewpoint'
      );

    default:
      return t(
        i18nLocale,
        'viewpointBuilder:navigation.sidebarTitle.openDataset',
        'Open dataset'
      );
  }
};

const getSidebarSubtitleFromContext = (
  state: ViewpointBuilderNavigationState,
  i18nLocale: I18nLocale
): string | undefined => {
  if (state.context === 'editViewpoint') {
    return t(
      i18nLocale,
      'viewpointBuilder:navigation.subtitle.editViewpoint',
      { viewpointName: state.viewpointName ?? '' },
      "Making changes to '{{viewpointName}}'."
    );
  }
  return undefined;
};

const selectTab = (
  state: ViewpointBuilderNavigationState,
  { tab }: SelectTabPayload
): ViewpointBuilderNavigationState => {
  return { ...state, selectedTab: tab };
};

const selectNavigationModeAndContext = (
  state: ViewpointBuilderNavigationState,
  { context }: SelectViewpointBuilderContextPayload
): ViewpointBuilderNavigationState => {
  return {
    ...state,
    context,
  };
};
const setComponentTypeAndCount = (
  state: ViewpointBuilderNavigationState,
  {
    selectedComponentTypes,
    selectedComponentCount,
  }: SetSelectedComponentTypeAndCountPayload
): ViewpointBuilderNavigationState => {
  const nextState: ViewpointBuilderNavigationState = {
    ...state,
    selectedComponentTypes,
    selectedComponentCount,
  };
  if (
    nextState.selectedTab === 'SUBGRAPH_TAB' &&
    nextState.selectedComponentCount > 0 &&
    isTabItemDisabled('SUBGRAPH_TAB', nextState)
  ) {
    return {
      ...nextState,
      selectedTab: 'SELECT_CONTEXT_COMPONENT_INSTANCES_TAB',
    };
  }
  return nextState;
};
const setGraphCountsAndComponentTypeRepresentation = (
  state: ViewpointBuilderNavigationState,
  {
    filtersCount,
    edgesCount,
    nodesCount,
    selectedComponentTypeRepresentation,
  }: SetGraphCountsAndComponentTypeRepresentationPayload
): ViewpointBuilderNavigationState => {
  return {
    ...state,
    filtersCount,
    edgesCount,
    nodesCount,
    selectedComponentTypeRepresentation,
  };
};
const setFormattingRulesCount = (
  state: ViewpointBuilderNavigationState,
  payload: SetFormattingRulesCount
): ViewpointBuilderNavigationState => {
  return {
    ...state,
    ...payload,
  };
};
const setGroupingRulesCount = (
  state: ViewpointBuilderNavigationState,
  { groupingRulesCount }: SetGroupingRulesCount
): ViewpointBuilderNavigationState => {
  return {
    ...state,
    groupingRulesCount,
  };
};
const setCollapsedPathsCount = (
  state: ViewpointBuilderNavigationState,
  { collapsedPathsCount }: SetCollapsedPathsCount
): ViewpointBuilderNavigationState => {
  return { ...state, collapsedPathsCount };
};
const setRequiredComponentsCount = (
  state: ViewpointBuilderNavigationState,
  { requiredComponentsCount }: SetRequiredComponentsCount
): ViewpointBuilderNavigationState => {
  return { ...state, requiredComponentsCount };
};
const setSavedViewpointDetails = (
  state: ViewpointBuilderNavigationState,
  { viewpointName }: SetSavedViewpointDetails
): ViewpointBuilderNavigationState => {
  return {
    ...state,
    viewpointName,
  };
};
const resetState = (): ViewpointBuilderNavigationState => getInitialState();

export const viewpointBuilderNavigationOperations = {
  getInitialState,
  selectTab,
  selectNavigationModeAndContext,
  composeViewModel,
  getInitialViewModel,
  getModeFromContext,
  setComponentTypeAndCount,
  setGraphCountsAndComponentTypeRepresentation,
  setFormattingRulesCount,
  setGroupingRulesCount,
  setCollapsedPathsCount,
  setRequiredComponentsCount,
  setSavedViewpointDetails,
  resetState,
};
